/** Stock video-perception chip catalog + serialize helpers for agent Edit. */

export type PerceptionChipKind =
  | "expression"
  | "appearance"
  | "environment"
  | "movement"
  | "quality";

export type PerceptionChipDef = {
  slug: string;
  label: string;
  kind: PerceptionChipKind;
  /** Optional cue text (custom chips). */
  description?: string;
  custom?: boolean;
};

export const PERCEPTION_CHIP_KINDS: Array<{ id: PerceptionChipKind; label: string }> = [
  { id: "expression", label: "Expression" },
  { id: "appearance", label: "Appearance" },
  { id: "environment", label: "Environment" },
  { id: "movement", label: "Movement" },
  { id: "quality", label: "Quality" },
];

/** Closed vocabulary shown in Chip vocabulary (23 stock labels). */
export const STOCK_PERCEPTION_CHIPS: PerceptionChipDef[] = [
  { slug: "neutral", label: "Neutral", kind: "expression" },
  { slug: "engaged", label: "Engaged", kind: "expression" },
  { slug: "smiling", label: "Smiling", kind: "expression" },
  { slug: "concerned", label: "Concerned", kind: "expression" },
  { slug: "frustrated", label: "Frustrated", kind: "expression" },
  { slug: "distracted", label: "Distracted", kind: "expression" },
  { slug: "face-visible", label: "Face visible", kind: "appearance" },
  { slug: "front-facing", label: "Front-facing", kind: "appearance" },
  { slug: "glasses", label: "Glasses", kind: "appearance" },
  { slug: "facial-hair", label: "Facial hair", kind: "appearance" },
  { slug: "headwear", label: "Headwear", kind: "appearance" },
  { slug: "home", label: "Home", kind: "environment" },
  { slug: "office", label: "Office", kind: "environment" },
  { slug: "car", label: "Car", kind: "environment" },
  { slug: "other-people", label: "Other people", kind: "environment" },
  { slug: "background-activity", label: "Background activity", kind: "environment" },
  { slug: "still", label: "Still", kind: "movement" },
  { slug: "fidgeting", label: "Fidgeting", kind: "movement" },
  { slug: "gesturing", label: "Gesturing", kind: "movement" },
  { slug: "leaving-frame", label: "Leaving frame", kind: "movement" },
  { slug: "well-lit", label: "Well lit", kind: "quality" },
  { slug: "low-light", label: "Low light", kind: "quality" },
  { slug: "partially-obscured", label: "Partially obscured", kind: "quality" },
];

/** Default selected set from the Perception redesign mock (18 of 23). */
export const DEFAULT_SELECTED_CHIP_SLUGS = STOCK_PERCEPTION_CHIPS
  .map((c) => c.slug)
  .filter(
    (s) =>
      ![
        "distracted",
        "headwear",
        "other-people",
        "leaving-frame",
        "partially-obscured",
      ].includes(s),
  );

const CUSTOM_PREFIX = "+";

export function slugifyChipLabel(label: string): string {
  return String(label || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 48) || "chip";
}

export function parsePerceptionChips(raw: string | undefined | null): {
  selected: Set<string>;
  custom: PerceptionChipDef[];
} {
  const selected = new Set<string>();
  const custom: PerceptionChipDef[] = [];
  const stockSlugs = new Set(STOCK_PERCEPTION_CHIPS.map((c) => c.slug));
  for (const line of String(raw || "").split(/\n+/)) {
    const t = line.trim();
    if (!t || t.startsWith("#")) continue;
    if (t.startsWith(CUSTOM_PREFIX)) {
      const body = t.slice(1);
      const parts = body.split("|");
      const kindRaw = parts[0] || "expression";
      const kind = (PERCEPTION_CHIP_KINDS.some((k) => k.id === kindRaw)
        ? kindRaw
        : "expression") as PerceptionChipKind;
      const slug = slugifyChipLabel(parts[1] || parts[2] || "custom");
      const label = (parts[2] || parts[1] || slug).trim() || slug;
      const description = parts.slice(3).join("|").trim() || undefined;
      if (!custom.some((c) => c.slug === slug)) {
        custom.push({
          slug,
          label,
          kind,
          description,
          custom: true,
        });
      }
      continue;
    }
    const slug = slugifyChipLabel(t);
    selected.add(slug);
    if (!stockSlugs.has(slug) && !custom.some((c) => c.slug === slug)) {
      custom.push({
        slug,
        label: t.replace(/-/g, " "),
        kind: "expression",
        custom: true,
      });
    }
  }
  return { selected, custom };
}

export function serializePerceptionChips(
  selected: Set<string>,
  custom: PerceptionChipDef[],
): string {
  const lines: string[] = [];
  for (const c of custom) {
    const desc = (c.description || "").replace(/\n/g, " ").trim();
    const label = (c.label || c.slug).replace(/\|/g, " ").trim();
    lines.push(`${CUSTOM_PREFIX}${c.kind}|${c.slug}|${label}|${desc}`);
  }
  for (const slug of selected) {
    lines.push(slug);
  }
  return lines.join("\n");
}

export type PerceptionRule = {
  id: string;
  text: string;
  enabled: boolean;
};

/** Disabled rules are stored as `# …` so offline video-perception can skip them. */
export function parsePerceptionRules(raw: string | undefined | null): PerceptionRule[] {
  const lines = String(raw || "")
    .split(/\n+/)
    .map((s) => s.trim())
    .filter(Boolean);
  if (!lines.length) return [];
  return lines.map((line, i) => {
    const disabled = line.startsWith("#") || line.startsWith("[-]");
    const text = disabled
      ? line.replace(/^#\s*/, "").replace(/^\[-\]\s*/, "").trim()
      : line;
    return { id: `rule-${i}-${text.slice(0, 24)}`, text, enabled: !disabled };
  });
}

export function serializePerceptionRules(rules: PerceptionRule[]): string {
  return rules
    .map((r) => {
      const t = r.text.trim();
      if (!t) return "";
      return r.enabled ? t : `# ${t}`;
    })
    .filter(Boolean)
    .join("\n");
}
